import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { Loader2 } from "lucide-react";
import { useFieldPresenceContext } from "./field-presence.context";

/**
 * Signale que le filtrage des champs est en cours, **par-dessus** les cases.
 *
 * ## Pourquoi en superposition, et pas ailleurs
 *
 * Deux versions ont précédé celle-ci. Un squelette remplaçant toute la grille :
 * la liste réapparaissait à un endroit que l'œil n'avait pas vu se construire.
 * Puis une ligne posée en tête de bloc, à côté de « Tout cocher » : elle
 * occupait de la place en permanence et attirait le regard loin de ce dont elle
 * parlait.
 *
 * Posée sur la grille, l'information est là où elle s'applique, et ne déplace
 * rien : la grille garde sa taille, les cases restent à leur place définitive,
 * et seules disparaissent ensuite celles qui n'ont pas de valeur.
 *
 * Volontairement en haut plutôt qu'au centre : la liste des options est plus
 * haute que la zone visible du drawer, un badge centré verticalement tomberait
 * souvent sous la ligne de flottaison.
 */
export function PresenceLoadingHint() {
  const vt = useVisibleT();
  const { isLoading } = useFieldPresenceContext();

  if (!isLoading) return null;

  return (
    <div
      className="absolute inset-0 z-10 flex items-start justify-center pt-10 pointer-events-none"
      aria-live="polite"
    >
      <span className="flex items-center gap-2 rounded-md border border-gray-200 bg-white/95 px-3 py-2 text-sm text-gray-600 shadow-sm">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {vt("bo.print.loadingFields", "Recherche des champs renseignés…")}
      </span>
    </div>
  );
}

/**
 * Classes à poser sur la grille des cases pendant le calcul : estompée et
 * inerte, mais toujours en place et à la bonne taille.
 */
export function presenceLoadingClassName(isLoading: boolean): string {
  return isLoading
    ? "opacity-40 pointer-events-none transition-opacity duration-200"
    : "transition-opacity duration-200";
}
